import { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getFirebaseFirestore } from "../config/firebase";

export const SCAN_LIMIT = 5;

export interface Plan {
    isPro: boolean;
    scanCount: number;
    stripeCustomerId?: string;
    loading: boolean;
}

export function usePlan(uid: string | null): Plan {
    const [plan, setPlan] = useState<Plan>({ isPro: false, scanCount: 0, loading: true });

    useEffect(() => {
        if (!uid) {
            setPlan({ isPro: false, scanCount: 0, loading: false });
            return;
        }

        let db: ReturnType<typeof getFirebaseFirestore>;
        try {
            db = getFirebaseFirestore();
        } catch {
            setPlan({ isPro: false, scanCount: 0, loading: false });
            return;
        }

        const ref = doc(db, "users", uid, "subscription", "plan");
        const unsub = onSnapshot(ref, (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                setPlan({
                    isPro: data.pro === true,
                    scanCount: data.scanCount ?? 0,
                    stripeCustomerId: data.stripeCustomerId,
                    loading: false,
                });
            } else {
                setPlan({ isPro: false, scanCount: 0, loading: false });
            }
        }, () => {
            setPlan({ isPro: false, scanCount: 0, loading: false });
        });

        return unsub;
    }, [uid]);

    return plan;
}

/**
 * Increments the scan count via the server endpoint, which enforces
 * SCAN_LIMIT_SERVER atomically. Throws if the limit is reached or the
 * request fails.
 */
export async function incrementScanCount(_uid: string): Promise<void> {
    try {
        const token = await getAuth().currentUser?.getIdToken();
        if (!token) throw new Error("Not authenticated");

        const apiBase = (import.meta.env as Record<string, string>).VITE_COMPILE_API_URL ?? "/api";
        const response = await fetch(`${apiBase}/scan/increment`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const json = await response.json() as { error?: string; upgrade?: boolean };
            throw new Error(json.error ?? "Failed to update scan count");
        }
    } catch (e) {
        console.error("[usePlan] Failed to increment scan count:", e);
        throw e;
    }
}
